<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'

import { useSourcepointStore } from '../../stores/sourcepoint'
import { useSessionStore } from '../../stores/session'
import { useClient } from '../../utils/client-only'
import AdaAdSlot from './AdaAdSlot.vue'

import type { Taboola, TaboolaSlotConfig } from '@xcel-next/rendering-tools'

type ActiveSlotConfig = Exclude<TaboolaSlotConfig, false>
type TaboolaWindow = Window & { _taboola?: object[] }

const props = defineProps<{
  taboolaConfig: Taboola
}>()

const { isClient } = useClient()
const sourcepointStore = useSourcepointStore()
const sessionStore = useSessionStore()

const shouldLoad = computed(() => sourcepointStore.consentsExist && !sessionStore.hasPurSub)

const taboolaSlotConfigs = computed<ActiveSlotConfig[]>(() =>
  props.taboolaConfig.slotConfigs
    ? Object.values(props.taboolaConfig.slotConfigs).filter((v): v is ActiveSlotConfig => v !== false && v !== undefined)
    : []
)

const taboolaScript = computed(() => props.taboolaConfig.script ?? '')
const taboolaScriptPrivacy = computed(() => props.taboolaConfig.scriptPrivacy ?? '')
const taboolaPixelUrl = computed(() => props.taboolaConfig.pixelUrl ?? '')
const pageContext = computed(() => props.taboolaConfig.pageContext ?? { article: 'auto' })

function injectLoader (id: string, src: string, privacySrc: string | null): void {
  if (document.getElementById(id)) return

  const tbScript = document.createElement('script')
  tbScript.src = src
  tbScript.async = true
  tbScript.id = id

  if (privacySrc) {
    tbScript.onerror = () => {
      tbScript.parentNode?.removeChild(tbScript)
      injectLoader(`${id}_fb`, privacySrc, null)
    }
  }

  const firstScript = document.getElementsByTagName('script')[0]
  firstScript.parentNode?.insertBefore(tbScript, firstScript)
}

function initTaboola (): void {
  const win = window as TaboolaWindow
  win._taboola = win._taboola ?? []
  const taboola = win._taboola

  taboola.push(pageContext.value)

  if (taboolaPixelUrl.value) {
    new Image().src = taboolaPixelUrl.value
  }

  injectLoader('tb_loader_script', taboolaScript.value, taboolaScriptPrivacy.value)

  window.performance?.mark?.('tbl_ic')

  taboolaSlotConfigs.value.forEach(slotConfig => {
    taboola.push({
      mode: slotConfig.mode,
      container: slotConfig.container,
      placement: slotConfig.placement,
      target_type: slotConfig.target_type
    })
  })

  taboola.push({ flush: true })
}

watch(shouldLoad, (newValue) => {
  if (newValue) initTaboola()
})

onMounted(() => {
  if (shouldLoad.value) initTaboola()
})
</script>

<template>
  <div v-if="isClient"
       id="bx-js-taboola"
       class="bx-content bx-ada bx-ads--taboola">
    <template v-for="(slotConfig, index) in taboolaSlotConfigs"
              :key="`taboola-container-${index}`">
      <div :id="slotConfig.container"
           class="bx-ada bx-ads--taboola" />

      <div v-if="index === 0"
           id="bx-js-ada-taboola-feed-slots"
           class="bx-ada bx-ada--taboola-feed-slots">
        <AdaAdSlot slot-name="tbsuperbanner"
                   :slot-id="1" />
        <AdaAdSlot slot-name="tbmobile"
                   :slot-id="1" />
      </div>
    </template>
  </div>
</template>
